import { internal } from './ConsoleLoggerService';

type Constructor<T = object> = new (...args: any[]) => T;
type ServiceContainer = { [key: string | symbol]: Constructor };
const services: ServiceContainer = {};
type SingletonContainer = { [key: string | symbol]: object };
const singletons: SingletonContainer = {};

/**
 * Fast non-cryptographic hash (djb2) for generating compact singleton keys.
 */
function hashString(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
}

/**
 * This method will add a service if a service with the same name does not already exist.
 * @param name name of the service to add
 * @param service the service class to add
 */
function addService<T extends object>(
  name: string | symbol,
  service: Constructor<T>,
) {
  if (services[name]) {
    internal.warn(`Service ${String(name)} already exists`, {
      as: services[name],
    });
    return;
  }
  services[name] = service;
}

/**
 * Get the service class implementation for the given service name
 * @param name the name of the service to get
 * @returns the service class implementation for the given service name
 */
function getService<T extends object>(name: string | symbol): Constructor<T> {
  return services[name] as Constructor<T>;
}

/**
 * Get the service instance for the given service name constructed with an empty constructor
 * @param name the name of the service to get
 * @returns the service instance implementation for the given service name
 */
function getSingleton<T extends object>(
  name: string | symbol,
  ...args: unknown[]
): T {
  const key = `${String(name)}:${hashString(JSON.stringify(args))}`;
  if (!singletons[key]) {
    const serviceType: Constructor<T> = services[name] as Constructor<T>;
    singletons[key] = new serviceType(...args);
  }
  return singletons[key] as T;
}

type ServicesProvider = {
  addService: typeof addService;
  getService: typeof getService;
  getSingleton: typeof getSingleton;
};

export const ServicesProvider: ServicesProvider = {
  addService,
  getService,
  getSingleton,
};
